// SIPP-632 / SIPP-729: GuidedReport wrapper + navigation shell
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Dialog,
  Stack,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useState, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useSelector } from 'react-redux'
import { UserSelector } from '../../../store/User'
import { StoryScreen1 } from './StoryScreen1'
import { StoryScreen2 } from './StoryScreen2'
import { StoryScreen3 } from './StoryScreen3'
import { StoryScreen4 } from './StoryScreen4'
import { StoryScreen5 } from './StoryScreen5'

const TOTAL_SCREENS = 5

const STORY_SEEN_KEY = (userId: string) => `wqi_story_seen_${userId}`

export const GuidedReportWrapper = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const location = useLocation()
  const navigate = useNavigate()
  const userId = useSelector(UserSelector.userId)

  const [open, setOpen] = useState(false)
  const [currentScreen, setCurrentScreen] = useState(1)

  useEffect(() => {
    if (!userId) return
    if (location.pathname !== '/profile/overview') return
    const seen = localStorage.getItem(STORY_SEEN_KEY(userId))
    if (!seen) {
      setOpen(true)
    }
  }, [userId, location.pathname])

  const dismiss = useCallback(() => {
    if (userId) {
      localStorage.setItem(STORY_SEEN_KEY(userId), 'true')
    }
    setOpen(false)
  }, [userId])

  const continueToRecommendations = useCallback(() => {
    dismiss()
    navigate('/profile/recommendations')
  }, [dismiss, navigate])

  const handleNext = () => {
    if (currentScreen < TOTAL_SCREENS) {
      setCurrentScreen((s) => s + 1)
    } else {
      dismiss()
    }
  }

  const handleBack = () => {
    if (currentScreen > 1) {
      setCurrentScreen((s) => s - 1)
    }
  }

  if (!open) return null

  const darkTeal = theme.palette.primary.dark

  return (
    <Dialog
      open={open}
      fullScreen={isMobile}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          ...(!isMobile && {
            borderRadius: 3,
            maxHeight: '90vh',
          }),
        },
      }}
    >
      {/* Header band */}
      <Box
        sx={{
          backgroundColor: darkTeal,
          px: 2,
          pt: 1.5,
          pb: 1.5,
        }}
      >
        {/* Progress bar — 5 pill segments */}
        <Stack direction="row" spacing={0.75}>
          {Array.from({ length: TOTAL_SCREENS }, (_, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                backgroundColor: i < currentScreen ? '#80acc7' : '#d9dbde',
              }}
            />
          ))}
        </Stack>

        {/* Counter + close button */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 1 }}
        >
          <Typography
            sx={{
              color: '#fff',
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            {currentScreen} of {TOTAL_SCREENS}
          </Typography>

          <Box
            component="button"
            onClick={dismiss}
            aria-label="Close guided report"
            sx={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </Box>
        </Stack>
      </Box>

      {/* Screen content */}
      <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', px: { xs: 2, md: 3 } }}>
        {currentScreen === 1 && <StoryScreen1 />}
        {currentScreen === 2 && <StoryScreen2 />}
        {currentScreen === 3 && <StoryScreen3 />}
        {currentScreen === 4 && <StoryScreen4 />}
        {currentScreen === 5 && <StoryScreen5 onContinue={continueToRecommendations} />}
      </Box>

      {/* Navigation footer — screen 5 owns its own buttons */}
      {currentScreen !== 5 && (
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        {currentScreen === 1 && (
          <Button
            variant="contained"
            fullWidth
            onClick={handleNext}
            sx={{
              backgroundColor: darkTeal,
              '&:hover': { backgroundColor: darkTeal },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            View My Report →
          </Button>
        )}

        {currentScreen >= 2 && currentScreen <= 4 && (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Button
              variant="text"
              onClick={handleBack}
              sx={{
                color: darkTeal,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              ← Back
            </Button>

            <Button
              variant="contained"
              onClick={handleNext}
              sx={{
                backgroundColor: darkTeal,
                '&:hover': { backgroundColor: darkTeal },
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Next →
            </Button>
          </Stack>
        )}

      </Box>
      )}
    </Dialog>
  )
}
